import { Injectable } from '@angular/core';
import { GoDaddyService } from '@vendasta/godaddy';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface DomainSearchResult {
  domain: string;
  available: boolean;
  parseError: string;
}

const secondLevelDomainOnlyRegex = `^(([A-Za-z0-9-]+).?([A-Za-z-]*))$`;
const acceptedTLDsRegex = `^(?!://)[a-zA-Z0-9][a-zA-Z0-9-_]+(.(com|info|net|org|biz|click|link|futbol|club|rocks|xyz))?$`;
export const acceptedTLDs = ['com', 'info', 'org', 'net', 'biz', 'click', 'link', 'futbol', 'club', 'rocks', 'xyz'];

@Injectable()
export class DynamicOrderFormService {
  constructor(private goDaddyService: GoDaddyService) {}

  getDomainSuggestions(domain: string, limit: number, tlds: string[]): Observable<DomainSearchResult[]> {
    return this.goDaddyService.getDomainSuggestionAndPrice(domain, tlds, limit).pipe(
      map((suggestions) => {
        if (suggestions) {
          return suggestions.map((suggestion) => {
            return {
              available: true,
              domain: suggestion.domain,
              parseError: '',
            };
          });
        }
        return [];
      }),
    );
  }

  checkDomainAvailability(domain: string): Observable<DomainSearchResult> {
    return this.goDaddyService.getDomainAvailable(domain).pipe(
      map((available) => {
        return {
          available: available || false,
          domain: domain,
          parseError: '',
        };
      }),
    );
  }

  getGoDaddyTermsOfService(domain: string): Observable<string> {
    const tld = domain.split('.')[1];
    return this.goDaddyService.getDomainAgreements([tld], false).pipe(
      map((agreements) => {
        return agreements[0].content;
      }),
    );
  }
}

// Parses valid domains and tlds. the inputUrl may or may not have the tld extension
export function parseDomain(inputUrl: string): DomainSearchResult {
  const validatedInput: DomainSearchResult = {
    available: false,
    domain: '',
    parseError: '',
  };

  if (inputUrl.startsWith('-') || inputUrl.endsWith('-')) {
    validatedInput.parseError = 'Domain names cannot start or end with a hyphen';
    return validatedInput;
  }

  if (!inputUrl.match(secondLevelDomainOnlyRegex)) {
    validatedInput.parseError = 'Invalid domain name. Must be in the format domain.com, for example';
    return validatedInput;
  }

  let inputTLD = acceptedTLDs.find((tld) => inputUrl.endsWith('.' + tld));
  // use .com by default if no TLD was entered
  if (!inputTLD) {
    inputUrl = inputUrl + '.com';
  } else {
    inputTLD = '.' + inputTLD;
  }

  const domainOnly = inputUrl.split(inputTLD)[0];
  if (domainOnly.endsWith('-') || inputUrl.endsWith('-')) {
    validatedInput.parseError = 'Domain names cannot end with a hyphen';
    return validatedInput;
  } else if (domainOnly.length > 63) {
    validatedInput.parseError = 'Domain name length must be 63 characters or less';
    return validatedInput;
  }

  if (!inputUrl.match(acceptedTLDsRegex)) {
    validatedInput.parseError = 'That domain extension is not supported. Please see above for supported extensions.';
    return validatedInput;
  }

  validatedInput.domain = inputUrl;
  validatedInput.available = true;
  return validatedInput;
}
